import { useState } from "react";
import { motion } from "framer-motion";
import NetworkIcon from "@core/network-icon";
import { AIFlowchartContent } from "../../types/ai-templates";

export function FlowchartTemplate({
  content,
  onUpdate,
}: {
  content: AIFlowchartContent;
  onUpdate: (newContent: AIFlowchartContent) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");

  // Handle step update
  const handleStepUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  const isReverse = (index: number) => Math.floor(index / 2) % 2 === 1;

  // Check if there's an odd number of steps
  const hasOddSteps = content.steps.length % 2 !== 0;
  const lastIndex = content.steps.length - 1;

  const getGridPosition = (index: number) => {
    // Special case for the last item if total steps count is odd
    if (hasOddSteps && index === lastIndex) {
      // Place the last item in the center (spanning both columns)
      const row = Math.floor(index / 2) + 1;
      return { row, col: "span" };
    }

    const row = Math.floor(index / 2) + 1;
    const col = index % 2 === 0 ? 1 : 2;

    // Flip the second row (or every odd row) for reverse layout
    return row % 2 === 1 ? { row, col } : { row, col: 3 - col };
  };

  // Helper function to determine arrow details
  const getArrowDetails = (index: number) => {
    const isLast = index === lastIndex;
    const nextIndex = index + 1;
    const reverse = isReverse(index);

    let arrowType = null;
    let arrowDirection = null;
    let arrowStyle = {};

    const { col } = getGridPosition(index);

    // Logic for arrow positioning
    if (hasOddSteps) {
      // Items pointing to the center card (the last two items before the center)
      if (index === lastIndex - 2 || index === lastIndex - 1) {
        arrowType = "to-center";

        // If item is on the left (col 1), arrow points right-down
        if (col === 1) {
          arrowDirection = "right-down";
          arrowStyle = {
            position: "absolute",
            bottom: "-40px",
            right: "0",
            transform: "rotate(45deg)",
          };
        }
        // If item is on the right (col 2), arrow points left-down
        else {
          arrowDirection = "left-down";
          arrowStyle = {
            position: "absolute",
            bottom: "-40px",
            left: "0",
            transform: "rotate(135deg)",
          };
        }
      }
      // Normal zigzag flow (not pointing to center)
      else if (
        !isLast &&
        nextIndex !== lastIndex - 1 &&
        nextIndex !== lastIndex
      ) {
        if (index % 2 === 0) {
          // Even indices have horizontal arrows
          arrowType = "horizontal";
          arrowStyle = {
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            ...(reverse
              ? { left: "-40px" } // arrow to left
              : { right: "-40px" }), // arrow to right
          };
          arrowDirection = reverse ? "left" : "right";
        } else {
          // Odd indices have downward arrows
          arrowType = "downward";
          arrowStyle = {
            position: "absolute",
            bottom: "-40px",
            left: "50%",
            transform: "translateX(-50%)",
          };
          arrowDirection = "down";
        }
      }
    } else {
      // For even number of steps, use the original zigzag logic
      if (!isLast) {
        if (index % 2 === 0) {
          // Even indices have horizontal arrows
          arrowType = "horizontal";
          arrowStyle = {
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            ...(reverse
              ? { left: "-40px" } // arrow to left
              : { right: "-40px" }), // arrow to right
          };
          arrowDirection = reverse ? "left" : "right";
        } else {
          // Odd indices have downward arrows
          arrowType = "downward";
          arrowStyle = {
            position: "absolute",
            bottom: "-40px",
            left: "50%",
            transform: "translateX(-50%)",
          };
          arrowDirection = "down";
        }
      }
    }

    return {
      arrowType,
      arrowDirection,
      arrowStyle,
    };
  };

  // Function to render the appropriate arrow SVG based on direction
  const renderArrowSVG = (direction: string, color: string) => {
    switch (direction) {
      case "right":
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 12H19M19 12L12 5M19 12L12 19"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "left":
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "down":
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 5V19M12 19L19 12M12 19L5 12"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "right-down":
      case "left-down":
        // These use the same SVG, just with different rotation in the style
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 12H19M19 12L12 5M19 12L12 19"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-2 gap-x-16 gap-y-20 p-10 relative">
      {content.steps.map((step, index) => {
        const isEditingTitle =
          editingIndex === index && editingField === "title";
        const isEditingDescription =
          editingIndex === index && editingField === "description";

        const genericColor =
          content.genericColors?.[index % content.genericColors.length] ||
          "#CBD5E0";
        const baseColor = step.color || "#A0AEC0"; // fallback if event.color missing
        const backgroundStyle = {
          background: `linear-gradient(135deg, ${baseColor}, ${genericColor})`,
        };

        const { row, col } = getGridPosition(index);
        const isLastItemOdd = hasOddSteps && index === lastIndex;

        const nextIndex = index + 1;

        // Get the next step's color for arrows
        const nextStep = content.steps[nextIndex];
        const nextColor = nextStep?.color || baseColor;

        // Get arrow details
        const { arrowType, arrowDirection, arrowStyle } =
          getArrowDetails(index);

        return (
          <div
            key={index}
            className={`relative flex flex-col items-center text-center ${
              isLastItemOdd
                ? "col-span-2"
                : col === "span"
                  ? "col-span-2"
                  : col === 1
                    ? "col-start-1"
                    : "col-start-2"
            }`}
            style={{
              gridRow: row,
              gridColumn: col === "span" ? "1 / span 2" : col,
            }}
          >
            {/* Render the appropriate arrow based on arrowType and arrowDirection */}
            {arrowType && arrowDirection && (
              <div
                style={{
                  ...arrowStyle,
                  color: nextColor,
                }}
              >
                {renderArrowSVG(arrowDirection, nextColor)}
              </div>
            )}

            {/* Step Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white dark:bg-gray-800 shadow-lg rounded-xl px-6 py-5 ${isLastItemOdd ? "max-w-xs mx-auto" : "w-full max-w-sm"}`}
            >
              {isEditingTitle ? (
                <input
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== step.title) {
                      handleStepUpdate(index, "title", tempValue);
                    }
                  }}
                  className="text-lg font-semibold w-full text-center dark:text-gray-300 dark:bg-gray-800"
                />
              ) : (
                <div className="flex flex-row justify-items-center">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white mb-3"
                    style={backgroundStyle}
                  >
                    {step.iconId ? (
                      <NetworkIcon
                        value={step.icon_description ?? step.iconId}
                        type="LUCIDE"
                      />
                    ) : (
                      <span className="font-bold">{index + 1}</span>
                    )}
                  </div>
                  <h4
                    className="font-semibold text-lg cursor-pointer flex-1"
                    onClick={() => {
                      setEditingIndex(index);
                      setEditingField("title");
                      setTempValue(step.title);
                    }}
                  >
                    {step.title}
                  </h4>
                </div>
              )}

              {isEditingDescription ? (
                <textarea
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== step.description) {
                      handleStepUpdate(index, "description", tempValue);
                    }
                  }}
                  className="text-sm mt-1 w-full text-center dark:text-gray-400 dark:bg-gray-800"
                  // on focus adjust text area height
                  onFocus={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
              ) : (
                <p
                  className="text-sm text-gray-600 dark:text-gray-400 mt-1 cursor-pointer"
                  onClick={() => {
                    setEditingIndex(index);
                    setEditingField("description");
                    setTempValue(step.description);
                  }}
                >
                  {step.description}
                </p>
              )}
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}
